import { useEffect, useRef } from "react";
import { useGetCompanionChatByIdLazyQuery } from "@axio/graphql/generated/graphql";
import { GENERAL_WORKSPACE_ID, HOME_ID } from "./useWorkspaceLists";

type ChatSummary = {
  id: string;
  name?: string | null;
  last_modified?: string | null;
  created?: string | null;
};

type WorkspaceThreadState = {
  chats: ChatSummary[];
  totalCount: number;
};

type CourseWorkspace = {
  id: string;
  chats?: ChatSummary[] | null;
};

interface UseResolveChatWorkspaceParams {
  routeChatId: string | undefined;
  generalChats: ChatSummary[];
  courseWorkspaces: CourseWorkspace[];
  threadStateByWorkspaceId: Record<string, WorkspaceThreadState>;
  setActiveThreadId: (id: string | null) => void;
  setActiveWorkspaceId: (id: string | null) => void;
  setOpenWorkspaceId: (id: string | null) => void;
  onLoadMoreThreads?: (workspaceId: string) => Promise<void>;
  refetchPreviewChats?: () => Promise<unknown>;
  activeWorkspaceId?: string | null;
}

export function useResolveChatWorkspace({
  routeChatId,
  generalChats,
  courseWorkspaces,
  threadStateByWorkspaceId,
  setActiveThreadId,
  setActiveWorkspaceId,
  setOpenWorkspaceId,
  onLoadMoreThreads,
  refetchPreviewChats,
  activeWorkspaceId,
}: UseResolveChatWorkspaceParams) {
  const [fetchChatById] = useGetCompanionChatByIdLazyQuery();
  const resolvedChatIdRef = useRef<string | null>(null);
  const searchingForChatRef = useRef<{ chatId: string; workspaceId: string } | null>(null);
  const isLoadingMoreRef = useRef<boolean>(false);
  const previewRefetchedChatIdRef = useRef<string | null>(null);

  // Effect to check if chat is found after loading more threads
  useEffect(() => {
    const searchState = searchingForChatRef.current;
    if (!searchState || isLoadingMoreRef.current) {
      return;
    }

    const { chatId, workspaceId } = searchState;
    const workspaceState = threadStateByWorkspaceId[workspaceId];

    // Check if chat is now in the loaded threads
    const isFound = workspaceState?.chats.some((chat) => chat.id === chatId) ?? false;

    if (isFound) {
      // Chat found! Mark as resolved and ensure workspace is opened
      resolvedChatIdRef.current = chatId;
      setActiveWorkspaceId(workspaceId);
      setOpenWorkspaceId(workspaceId);
      searchingForChatRef.current = null;
      return;
    }

    // Chat not found yet - check if there are more threads to load
    if (workspaceState) {
      const hasMore = workspaceState.chats.length < workspaceState.totalCount;
      if (hasMore && onLoadMoreThreads) {
        // Continue loading more threads
        isLoadingMoreRef.current = true;
        void (async () => {
          try {
            await onLoadMoreThreads(workspaceId);
          } finally {
            isLoadingMoreRef.current = false;
          }
        })();
      } else {
        // No more threads to load, but chat still not found
        // This shouldn't happen if the chat exists, but handle gracefully
        searchingForChatRef.current = null;
      }
    }
  }, [threadStateByWorkspaceId, setActiveWorkspaceId, setOpenWorkspaceId, onLoadMoreThreads]);

  useEffect(() => {
    if (!routeChatId) {
      resolvedChatIdRef.current = null;
      searchingForChatRef.current = null;
      previewRefetchedChatIdRef.current = null;
      setActiveThreadId(null);
      return;
    }

    // Prevent re-resolving the same chat
    if (resolvedChatIdRef.current === routeChatId && activeWorkspaceId !== HOME_ID) {
      return;
    }

    setActiveThreadId(routeChatId);

    // Always check general chats first when resolving an existing chat (routeChatId exists)
    // This ensures we can find general chats even when currently in a course workspace
    const isInGeneralChats = generalChats.some((chat) => chat.id === routeChatId);
    if (isInGeneralChats) {
      resolvedChatIdRef.current = routeChatId;
      setActiveWorkspaceId(GENERAL_WORKSPACE_ID);
      setOpenWorkspaceId(GENERAL_WORKSPACE_ID);
      searchingForChatRef.current = null;
      return;
    }

    const workspace = courseWorkspaces.find((w) => w.chats?.some((chat) => chat.id === routeChatId));
    if (workspace) {
      resolvedChatIdRef.current = routeChatId;
      setActiveWorkspaceId(workspace.id);
      setOpenWorkspaceId(workspace.id);
      searchingForChatRef.current = null;
      return;
    }

    // Also check in the full thread state (already loaded threads)
    const workspaceFromState = Object.entries(threadStateByWorkspaceId).find(([, state]) =>
      state.chats.some((chat) => chat.id === routeChatId)
    );
    if (workspaceFromState) {
      resolvedChatIdRef.current = routeChatId;
      setActiveWorkspaceId(workspaceFromState[0]);
      setOpenWorkspaceId(workspaceFromState[0]);
      searchingForChatRef.current = null;
      return;
    }

    // Chat not found in preview data or loaded threads - fetch it to get workspace info
    void (async () => {
      try {
        const result = await fetchChatById({ variables: { id: routeChatId } });
        const chatData = result.data?.getCompanionChatById;
        if (!chatData) {
          return;
        }

        const chatWorkspace = chatData.data?.workspacesHasChat?.[0];
        const targetWorkspaceId = chatWorkspace?.id ?? GENERAL_WORKSPACE_ID;
        const hasWorkspaceInPreview =
          Boolean(chatWorkspace?.id) && courseWorkspaces.some((w) => w.id === targetWorkspaceId);

        if (
          chatWorkspace?.id &&
          !hasWorkspaceInPreview &&
          refetchPreviewChats &&
          previewRefetchedChatIdRef.current !== routeChatId
        ) {
          previewRefetchedChatIdRef.current = routeChatId;
          await refetchPreviewChats();
        }

        if (chatWorkspace?.id) {
          const currentWorkspace = activeWorkspaceId;
          if (
            !currentWorkspace ||
            currentWorkspace === GENERAL_WORKSPACE_ID ||
            currentWorkspace === HOME_ID ||
            currentWorkspace === targetWorkspaceId
          ) {
            setActiveWorkspaceId(targetWorkspaceId);
            setOpenWorkspaceId(targetWorkspaceId);
          } else {
            searchingForChatRef.current = { chatId: routeChatId, workspaceId: currentWorkspace };
            if (onLoadMoreThreads) {
              isLoadingMoreRef.current = true;
              try {
                await onLoadMoreThreads(currentWorkspace);
              } finally {
                isLoadingMoreRef.current = false;
              }
            }
            return;
          }
        } else {
          const currentWorkspace = activeWorkspaceId;
          if (currentWorkspace && currentWorkspace !== GENERAL_WORKSPACE_ID && currentWorkspace !== HOME_ID) {
            searchingForChatRef.current = { chatId: routeChatId, workspaceId: currentWorkspace };
            if (onLoadMoreThreads) {
              isLoadingMoreRef.current = true;
              try {
                await onLoadMoreThreads(currentWorkspace);
              } finally {
                isLoadingMoreRef.current = false;
              }
            }
            return;
          }
        }

        // Start searching for the chat by loading more threads
        searchingForChatRef.current = { chatId: routeChatId, workspaceId: targetWorkspaceId };

        // Trigger initial load more threads
        if (onLoadMoreThreads) {
          isLoadingMoreRef.current = true;
          try {
            await onLoadMoreThreads(targetWorkspaceId);
          } finally {
            isLoadingMoreRef.current = false;
          }
        }
      } catch (error) {
        console.error("Error fetching chat for workspace resolution:", error);
        searchingForChatRef.current = null;
      }
    })();
  }, [
    routeChatId,
    generalChats,
    courseWorkspaces,
    threadStateByWorkspaceId,
    setActiveThreadId,
    setActiveWorkspaceId,
    setOpenWorkspaceId,
    onLoadMoreThreads,
    refetchPreviewChats,
    fetchChatById,
    activeWorkspaceId,
  ]);
}
